import { useId, useMemo } from "react";
import type { CSSProperties, ComponentProps } from "react";
import { Legend, ResponsiveContainer, Tooltip } from "recharts";
import { cn } from "../../lib/utils";
import { ChartContext } from "./context";
import type { ChartConfig } from "./context";
import { ChartLegendContent } from "./ChartLegendContent";
import { ChartStyle } from "./ChartStyle";
import { ChartTooltipContent } from "./ChartTooltipContent";
import { ChartTooltipIndicator } from "./ChartTooltipIndicator";
import { ChartTooltipLabel } from "./ChartTooltipLabel";
import { ChartTooltipName } from "./ChartTooltipName";
import { ChartTooltipRows } from "./ChartTooltipRows";
import { ChartTooltipValue } from "./ChartTooltipValue";

export interface ChartProps extends ComponentProps<"div"> {
  /** Per-series display config (label/icon + color or per-theme colors), keyed by series key. */
  config: ChartConfig;
  children: ComponentProps<typeof ResponsiveContainer>["children"];
}

/**
 * Responsive wrapper for a recharts chart: provides the series config to `Chart.Tooltip` /
 * `Chart.Legend` content, emits per-series `--color-<key>` variables (use `var(--color-<key>)`
 * as a fill/stroke), and normalizes recharts' default grays to design tokens. Compose recharts
 * primitives inside; for standard chart shapes prefer the PieChart/BarChart/LineChart molecules.
 */
const ChartRoot = ({ id, className, children, config, style, ...props }: ChartProps) => {
  const uniqueId = useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

  // Plain-color series surface as inline --color-<key> variables here; only per-theme series need
  // the <ChartStyle> tag (see its comment). The consumer's style prop wins on conflicts.
  const colorVars = useMemo(() => {
    const vars: CSSProperties = {};
    for (const [key, itemConfig] of Object.entries(config)) {
      if (itemConfig.color) vars[`--color-${key}`] = itemConfig.color;
    }
    return vars;
  }, [config]);

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot="chart"
        data-chart={chartId}
        style={{ ...colorVars, ...style }}
        className={cn(
          "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex aspect-video justify-center text-xs [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
          className
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
};

export const Chart = Object.assign(ChartRoot, {
  /** recharts `Tooltip` — pass `content={<Chart.TooltipContent />}`. */
  Tooltip,
  TooltipContent: ChartTooltipContent,
  TooltipLabel: ChartTooltipLabel,
  TooltipRows: ChartTooltipRows,
  TooltipIndicator: ChartTooltipIndicator,
  TooltipName: ChartTooltipName,
  TooltipValue: ChartTooltipValue,
  /** recharts `Legend` — pass `content={<Chart.LegendContent />}`. */
  Legend,
  LegendContent: ChartLegendContent,
});
